import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CheckCircle2, Clock, ShieldCheck, XCircle, AlertCircle, Save, Users, MapPin, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const STATUSES = [
  { key: "present",     label: "Present",     icon: CheckCircle2, bg: "bg-emerald-500 hover:bg-emerald-600",   ring: "ring-emerald-500" },
  { key: "late",        label: "Late",        icon: Clock,        bg: "bg-amber-500 hover:bg-amber-600",       ring: "ring-amber-500" },
  { key: "justified",   label: "Justified",   icon: ShieldCheck,  bg: "bg-sky-500 hover:bg-sky-600",           ring: "ring-sky-500" },
  { key: "unjustified", label: "Unjustified", icon: XCircle,      bg: "bg-[#C8102E] hover:bg-[#a40c25]",       ring: "ring-[#C8102E]" },
];

// Local-time ISO date helpers (avoid UTC drift on toISOString())
const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const isoWeekday = (d) => (d.getDay() + 6) % 7; // Mon=0..Sun=6

function isHolidayDate(dateStr, holidays) {
  const d = parseISO(dateStr);
  return holidays.some((h) => {
    if (!h?.date) return false;
    const hd = parseISO(h.date);
    if (h.recurring_annual) return hd.getMonth() === d.getMonth() && hd.getDate() === d.getDate();
    return h.date === dateStr;
  });
}

function isValidClassDay(cls, dateStr, holidays) {
  if (!cls?.schedule?.length) return false;
  const d = parseISO(dateStr);
  if (cls.start_date && d < parseISO(cls.start_date)) return false;
  if (cls.end_date && d > parseISO(cls.end_date)) return false;
  if (!cls.schedule.some((b) => b.weekday === isoWeekday(d))) return false;
  if (isHolidayDate(dateStr, holidays)) return false;
  return true;
}

function nextClassDay(cls, holidays, fromDateStr) {
  if (!cls?.schedule?.length) return null;
  const today = parseISO(fromDateStr);
  const start = cls.start_date ? parseISO(cls.start_date) : null;
  const end = cls.end_date ? parseISO(cls.end_date) : null;
  let cursor = new Date(today);
  if (start && cursor < start) cursor = new Date(start);
  for (let i = 0; i < 400; i++) {
    if (end && cursor > end) return null;
    const s = isoDate(cursor);
    if (isValidClassDay(cls, s, holidays)) return s;
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
}

function StatusButtons({ value, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
      {STATUSES.map((s) => {
        const Icon = s.icon;
        const active = value === s.key;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onChange(s.key)}
            data-testid={`${testidPrefix}-${s.key}`}
            className={`flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold transition-all ${
              active
                ? `${s.bg} text-white shadow-md ring-2 ring-offset-1 ${s.ring}`
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            <Icon className="h-3.5 w-3.5" /> {s.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AttendancePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [classId, setClassId] = useState(params.get("class_id") || "");
  const [date, setDate] = useState(params.get("date") || isoDate(new Date()));
  const [register, setRegister] = useState(null);
  const [originalKey, setOriginalKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Treat URL ?date= as explicit; otherwise auto-snap on class change / data load.
  const userPickedDateRef = useRef(!!params.get("date"));

  const canEdit = ["admin", "receptionist", "teacher"].includes(user?.role);

  useEffect(() => {
    http.get("/classes?status=active&limit=200").then(({ data }) => setClasses(data.items || []));
    http.get("/holidays").then(({ data }) => setHolidays(data.items || data || [])).catch(() => setHolidays([]));
  }, []);

  // Auto-snap date to next class day when class changes or data loads,
  // unless the user explicitly picked a date.
  useEffect(() => {
    if (userPickedDateRef.current) return;
    if (!classId || !classes.length) return;
    const cls = classes.find((c) => c.id === classId);
    if (!cls) return;
    const today = isoDate(new Date());
    const baseDate = date || today;
    if (isValidClassDay(cls, baseDate, holidays)) return;
    const snapped = nextClassDay(cls, holidays, today);
    if (snapped && snapped !== date) {
      setDate(snapped);
      toast.info(t("attendance.snappedToNextClassDay"));
    }
  }, [classId, classes, holidays]); // eslint-disable-line

  useEffect(() => {
    // sync URL
    const p = new URLSearchParams();
    if (classId) p.set("class_id", classId);
    if (date) p.set("date", date);
    setParams(p, { replace: true });
  }, [classId, date]); // eslint-disable-line

  useEffect(() => {
    if (classId) loadStudents(classId);
  }, [classId]); // eslint-disable-line

  const loadStudents = async (cid) => {
    try {
      const { data } = await http.get(`/classes/${cid}/roster`);
      const map = {};
      (data.roster || []).forEach((s) => { map[s.id] = s; });
      setStudents(map);
    } catch (e) { /* ignore */ }
  };

  const loadRegister = async () => {
    if (!classId || !date) return;
    setLoading(true); setError(null); setRegister(null);
    try {
      const { data } = await http.get(`/attendance/register?class_id=${classId}&date=${date}`);
      setRegister(data);
      setOriginalKey(JSON.stringify(data.records || []));
      // Load parent notices for this date+class roster
      const cls = classes.find((c) => c.id === classId);
      if (cls) {
        const sids = cls.enrolled_student_ids || [];
        const { data: nd } = await http.get(`/absence-notices?from=${date}&to=${date}`);
        setNotices((nd.items || []).filter((n) => sids.includes(n.student_id)));
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "date_is_holiday") setError({ kind: "holiday", name: det.holiday_name });
      else if (det?.error === "not_a_class_day") setError({ kind: "not_class_day" });
      else if (e?.response?.status === 403) setError({ kind: "forbidden" });
      else setError({ kind: "other", msg: det || "Failed to load" });
    } finally { setLoading(false); }
  };

  const updateRecord = (sid, patch) => {
    setRegister((r) => ({
      ...r,
      records: r.records.map((rec) => rec.student_id === sid ? { ...rec, ...patch } : rec),
    }));
  };

  const applyNotice = (n) => {
    updateRecord(n.student_id, {
      status: "justified",
      comment: `Parent notice (${n.reason}): ${n.reason_detail || ""}`.trim(),
    });
    toast.success(t("attendance.noticeApplied"));
  };

  const markAllPresent = () => {
    setRegister((r) => ({ ...r, records: r.records.map((rec) => ({ ...rec, status: "present", minutes_late: 0 })) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await http.post("/attendance/register", {
        class_id: classId, meeting_date: date,
        records: register.records, lesson_notes: register.lesson_notes || "",
      });
      setRegister(data); setOriginalKey(JSON.stringify(data.records || []));
      const justified = (data.auto_justified || []).length;
      toast.success(`${t("attendance.saved")} (${data.records.length} ${t("attendance.studentsRecorded")}${justified ? ` · ${justified} auto-justified` : ""})`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.error === "register_locked" ? t("attendance.lockedError") : "Save failed");
    } finally { setSaving(false); }
  };

  const dirty = register && JSON.stringify(register.records) !== originalKey;
  const cls = classes.find((c) => c.id === classId);
  const pendingNotices = notices.filter((n) => n.status === "pending");

  // Unsaved-changes guard
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  return (
    <PageChrome title={t("attendance.title")} subtitle={t("attendance.subtitle")}>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="min-w-[260px] flex-1">
          <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{t("classes.title")}</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="bg-white" data-testid="attendance-class-select"><SelectValue placeholder={t("attendance.pickClass")}/></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 mb-1 block">{t("attendance.date")}</label>
          <Input type="date" value={date} onChange={(e) => { userPickedDateRef.current = true; setDate(e.target.value); }} className="bg-white" data-testid="attendance-date-input"/>
        </div>
        <Button onClick={loadRegister} disabled={!classId || loading} className="bg-[#0A1F44] hover:bg-[#0A1F44]/90"
                data-testid="attendance-load-button">
          {loading ? "..." : t("attendance.loadRegister")}
        </Button>
      </div>

      {error && (
        <Card className="bg-amber-50 border-amber-200 p-5 mb-6 flex gap-3 items-center" data-testid="attendance-error">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0"/>
          <div className="text-sm">
            {error.kind === "holiday" && <span><b>{t("attendance.holidayHit")}</b> — {error.name}</span>}
            {error.kind === "not_class_day" && <span>{t("attendance.notClassDay")}</span>}
            {error.kind === "forbidden" && <span>{t("attendance.forbidden")}</span>}
            {error.kind === "other" && <span>{String(error.msg)}</span>}
          </div>
        </Card>
      )}

      {register && cls && (
        <>
          <Card className="bg-white p-4 mb-4 flex flex-wrap items-center gap-4 justify-between">
            <div className="space-y-1">
              <h2 className="font-serif text-xl text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>{cls.name}</h2>
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3"/> {date}</span>
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3"/> {register.records.length}</span>
                <Badge variant={register.status === "locked" ? "destructive" : "secondary"}>{register.status}</Badge>
              </div>
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={markAllPresent} data-testid="attendance-bulk-present">
                  {t("attendance.bulkPresent")}
                </Button>
              </div>
            )}
          </Card>

          {pendingNotices.length > 0 && (
            <Card className="bg-sky-50 border-sky-200 p-4 mb-4" data-testid="attendance-notices-card">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sky-900 text-sm">
                  {pendingNotices.length} {t("attendance.parentNotices")}
                </span>
              </div>
              <ul className="space-y-1.5">
                {pendingNotices.map((n) => (
                  <li key={n.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-700">
                      <b>{students[n.student_id]?.first_name} {students[n.student_id]?.last_name || ""}</b>
                      <span className="text-slate-500"> — {n.reason} {n.reason_detail && ` · ${n.reason_detail}`}</span>
                    </span>
                    <Button variant="outline" size="sm" onClick={() => applyNotice(n)}
                            data-testid={`attendance-apply-notice-${n.id}`}>{t("attendance.apply")}</Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="attendance-table">
            {register.records.map((rec) => {
              const s = students[rec.student_id] || {};
              return (
                <div key={rec.student_id} className="p-4 grid gap-3 md:grid-cols-[1fr_auto_160px] md:items-center">
                  <div className="space-y-0.5">
                    <div className="font-medium text-[#0A1F44]">{s.first_name} {s.last_name}</div>
                    {s.has_alerts && <Badge variant="destructive" className="text-[10px]">⚠ medical alert</Badge>}
                  </div>
                  <StatusButtons
                    value={rec.status}
                    onChange={canEdit ? (v) => updateRecord(rec.student_id, { status: v, minutes_late: v === "late" ? rec.minutes_late || 5 : 0 }) : () => {}}
                    testidPrefix={`attendance-status-${rec.student_id}`}
                  />
                  {rec.status === "late" ? (
                    <Input type="number" min={0} max={120} value={rec.minutes_late || 0}
                           onChange={(e) => updateRecord(rec.student_id, { minutes_late: Number(e.target.value) })}
                           placeholder="min late" data-testid={`attendance-late-${rec.student_id}`}/>
                  ) : <span className="hidden md:block"></span>}
                  <Input className="md:col-span-3" placeholder={t("attendance.commentPlaceholder")}
                         value={rec.comment || ""}
                         onChange={(e) => updateRecord(rec.student_id, { comment: e.target.value })}/>
                </div>
              );
            })}
          </div>

          <Card className="bg-white p-4 mt-4">
            <label className="text-sm font-medium text-slate-700">{t("attendance.lessonDiary")}</label>
            <Textarea className="mt-1" rows={3} value={register.lesson_notes || ""}
                      onChange={(e) => setRegister((r) => ({ ...r, lesson_notes: e.target.value }))}
                      data-testid="attendance-lesson-diary"/>
          </Card>

          {canEdit && (
            <div className="sticky bottom-0 mt-6 -mx-6 px-6 py-4 bg-white border-t border-slate-200 shadow-lg flex items-center justify-between">
              <span className="text-sm text-slate-500">{dirty ? t("attendance.unsaved") : t("attendance.allSaved")}</span>
              <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8102E] hover:bg-[#a40c25]"
                      data-testid="attendance-save-button">
                <Save className="h-4 w-4 mr-2"/>{t("attendance.saveRegister")}
              </Button>
            </div>
          )}
        </>
      )}
    </PageChrome>
  );
}
